import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "count", "loading", "nameField", "centerSelect",
    "sessionSelect", "sessionInfo", "sessionInfoTitle", "sessionInfoMeta",
    "sessionInfoProgress", "sessionInfoRemaining"
  ]
  static values = { url: String }

  connect() {
    this.timeout = null
    this.abortController = null
    if (this.hasSessionSelectTarget && this.sessionSelectTarget.value) {
      this.sessionSelected()
    }
    this.updatePreview()
  }

  disconnect() {
    clearTimeout(this.timeout)
    this.abortController?.abort()
  }

  sessionSelected() {
    const select = this.sessionSelectTarget
    const option = select.options[select.selectedIndex]

    if (!select.value) {
      if (this.hasNameFieldTarget) {
        this.nameFieldTarget.readOnly = false
        this.nameFieldTarget.classList.remove("bg-surface-2", "text-text-muted", "cursor-default")
        this.nameFieldTarget.classList.add("bg-surface-1")
      }
      if (this.hasSessionInfoTarget) this.sessionInfoTarget.classList.add("hidden")
      this.suggestName()
      return
    }

    const { title, center, date, remaining, fillPct, centerId } = option.dataset
    const rem = parseInt(remaining, 10)

    if (this.hasNameFieldTarget) {
      const shortTitle = title.length > 38 ? title.substring(0, 38).trimEnd() + "…" : title
      const [d, m, y] = date.split("/")
      const monthYear = new Intl.DateTimeFormat("fr-FR", { month: "short", year: "2-digit" }).format(new Date(y, m - 1, d))
      this.nameFieldTarget.value = `${shortTitle} · ${center} · ${monthYear}`
      this.nameFieldTarget.readOnly = true
      this.nameFieldTarget.classList.add("bg-surface-2", "text-text-muted", "cursor-default")
      this.nameFieldTarget.classList.remove("bg-surface-1")
    }

    if (this.hasCenterSelectTarget && centerId) {
      this.centerSelectTarget.value = centerId
    }

    if (this.hasSessionInfoTarget) {
      this.sessionInfoTarget.classList.remove("hidden")
      if (this.hasSessionInfoTitleTarget) this.sessionInfoTitleTarget.textContent = title
      if (this.hasSessionInfoMetaTarget) this.sessionInfoMetaTarget.textContent = `${center} · ${date}`
      if (this.hasSessionInfoRemainingTarget) this.sessionInfoRemainingTarget.textContent = rem
      if (this.hasSessionInfoProgressTarget) {
        const pct = parseInt(fillPct, 10) || 0
        this.sessionInfoProgressTarget.style.width = `${Math.min(pct, 100)}%`
      }
    }
  }

  suggestName() {
    if (!this.hasNameFieldTarget || this.nameFieldTarget.readOnly) return

    const targetSelect = this.element.querySelector("[name='campaign[criteria][target_type]']")
    const deciderCheckbox = this.element.querySelector("[name='campaign[criteria][is_decision_maker]']")

    const targetLabels = {
      prospects: "Prospects", clients: "Clients",
      dormants: "Dormants", fantomes: "Fantômes", recurrents: "Récurrents"
    }
    const target = targetSelect?.value ? (targetLabels[targetSelect.value] || "Tous") : "Tous"
    const decider = deciderCheckbox?.checked ? "Décideurs" : null

    const center = this.hasCenterSelectTarget && this.centerSelectTarget.selectedIndex > 0
      ? this.centerSelectTarget.options[this.centerSelectTarget.selectedIndex].text
      : null

    const month = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(new Date())
    const parts = [target, decider, center, month.charAt(0).toUpperCase() + month.slice(1)].filter(Boolean)
    this.nameFieldTarget.value = parts.join(" · ")
  }

  updatePreview() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.fetchPreview(), 300)
  }

  async fetchPreview() {
    if (!this.hasCountTarget) return

    const form = this.element.tagName === "FORM" ? this.element : this.element.closest("form")
    if (!form) return

    this.abortController?.abort()
    this.abortController = new AbortController()

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }

    try {
      const formData = new FormData(form)
      const params = new URLSearchParams(formData).toString()
      const response = await fetch(`${this.urlValue}?${params}`, {
        signal: this.abortController.signal
      })

      if (response.ok) {
        const data = await response.json()
        this.countTarget.textContent = data.count
      } else {
        this.countTarget.textContent = "—"
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        this.countTarget.textContent = "—"
      }
    } finally {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add("hidden")
      }
    }
  }
}
